import { Input } from "@/components/ui/input";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { PhoneInput } from "@/components/form/phone-input";
import { SocialSecurityNumberInput } from "@/components/form/social-security-number-input";
import { StateSelect } from "@/components/form/state-select";
import { PostalCodeInput } from "@/components/form/postal-code-input";
import { Button } from "@/components/ui/button-tw";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { useState } from "react";
import dayjs from "dayjs";
import { useAuthUserApplication } from "@/hooks/tenancy/use-auth-user-application";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import { cn } from "@/utils/classnames";
import { useAuth } from "@/providers/auth";
import { DateInput } from "@/components/ui/datefield-rac";
import { CalendarIcon } from "lucide-react";
import {
  Button as RAButton,
  DatePicker as RADatePicker,
  Group,
} from "react-aria-components";
import { useCustomCountdown } from "../../hooks/useCountDown";

const formatPhoneNumber = (value) => {
  let phone = ("" + value).replace(/\D/g, "");
  const match = phone.match(/^(\d{1,3})(\d{0,3})(\d{0,4})$/);
  if (match) {
    phone = `${match[1]}${match[2] ? "-" : ""}${match[2]}${
      match[3] ? "-" : ""
    }${match[3]}`;
  }
  return phone;
};

const formatSocialSecurityNumber = (value) => {
  let socialSecurity = ("" + value).replace(/\D/g, "");
  const match = socialSecurity.match(/^(\d{1,3})(\d{0,2})(\d{0,4})$/);
  if (match) {
    socialSecurity = `${match[1]}${match[2] ? "-" : ""}${match[2]}${
      match[3] ? "-" : ""
    }${match[3]}`;
  }
  return socialSecurity;
};

const schema = yup
  .object({
    firstName: yup.string().required("First name is a required field"),
    middleName: yup.string().optional(),
    lastName: yup.string().required("Last name is a required field"),
    email: yup
      .string()
      .email("Email must be a valid email")
      .required("Email is a required field"),
    phone: yup.string().required("Phone number is a required field"),
    socialSecurity: yup
      .string()
      .required("Social security number is a required field"),
    birth: yup.date().required(),
    address: yup.string().required("Address is a required field"),
    city: yup.string().required("City is a required field."),
    state: yup.string().required("State is a required field."),
    postalCode: yup.string().required("Postal code is a required field."),
  })
  .required();

export default function AuthUserStart() {
  const queryClient = useQueryClient();
  const [count, { start: startCount }] = useCustomCountdown(0, 1000);
  const { email: userEmail } = useAuth();
  const { mutate, isLoading, isSuccess } = useAuthUserApplication();
  const {
    control,
    register,
    handleSubmit,
    getValues,
    reset,
    formState: { errors, isValid },
  } = useForm({
    mode: "onBlur",
    resolver: yupResolver(schema),
    defaultValues: {
      firstName: "",
      middleName: "",
      lastName: "",
      email: "",
      phone: "",
      socialSecurity: "",
      address: "",
      city: "",
      state: "",
      postalCode: "",
    },
    values: {
      email: userEmail,
    },
  });
  const [step, setStep] = useState(0);

  const onSubmit = (values) => {
    const payload = {
      name: {
        first_name: values.firstName,
        middle_name: values.middleName,
        last_name: values.lastName,
      },
      email_address: values.email,
      personal_phone_number: values.phone,
      social_security_number: values.socialSecurity,
      date_of_birth: dayjs(values.birth?.toString()).format("YYYY-MM-DD"),
      address: {
        street_line_1: values.address,
        city: values.city,
        state: values.state,
        postal_code: values.postalCode,
        country: "US",
      },
    };

    mutate(payload, {
      onSuccess: () => {
        startCount(5000);
        setTimeout(() => {
          setStep(2);
          toast.success(`Form submitted successfully!`);
          reset();
        }, 5000);
      },
      onError: () => {
        toast.error(
          "Something went wrong on form submission. Please try again.",
          {
            autoClose: 10000,
            hideProgressBar: false,
          }
        );
      },
    });
  };

  return (
    <div className="flex w-full min-h-full bg-gray-50 relative">
      <div className="bg-skin-primary w-[500px] p-8 fixed h-screen flex flex-col justify-between">
        <div>
          <h1 className="text-white text-2xl font-semibold">
            Become an Authorized User
          </h1>
          <div className="mt-4 text-white font-medium">
            <p className="text-white">
              As an Authorized User, you'll be able to:
            </p>
            <ul className="mt-3">
              <li className="flex items-center text-sm">
                <ArrowRight className="w-4 h-4 mr-2" />
                Move money
              </li>
              <li className="flex items-center text-sm">
                <ArrowRight className="w-4 h-4 mr-2" />
                Manage invoices and contacts
              </li>
              <li className="flex items-center text-sm">
                <ArrowRight className="w-4 h-4 mr-2" />
                Add payment methods
              </li>
            </ul>
          </div>
          <div className="text-white mt-24 font-medium w-full">
            <h4>Getting started is easy!</h4>
            <ol className="overflow-hidden mt-4 space-y-4">
              <li className="flex items-center gap-x-4">
                <div className=" bg-white rounded-full border px-1 py-1"></div>
                <div>
                  <p className="text-sm ">Share a few details</p>
                  <p className="text-sm font-normal">
                    We'll ask for some basic personal information to verify your
                    identity.
                  </p>
                </div>
              </li>
              <li className="flex items-center gap-x-4">
                <div
                  className={cn("rounded-full border px-1 py-1", {
                    "bg-white": step === 2,
                  })}
                ></div>
                <div>
                  <p className="text-sm ">We'll take it from here</p>
                  <p className="text-sm font-normal">
                    Our team will review and process your application.
                  </p>
                </div>
              </li>
              <li className="flex items-center gap-x-4">
                <div className=" rounded-full border px-1 py-1"></div>
                <div>
                  <p className="text-sm ">Get notified</p>
                  <p className="text-sm font-normal">
                    Once the review is complete, we'll let you know if you've
                    been approved and what's next.
                  </p>
                </div>
              </li>
            </ol>
          </div>
        </div>
        <div>
          <h6 className="text-sm font-medium text-white">
            Important: Why we need your information
          </h6>
          <p className="text-xs font-medium text-white mt-1">
            Prior to engaging in the activities permitted to an Authorized User,
            Pocketbook, on behalf of the Bank, must take steps to obtain,
            verify, and record information that identifies individuals who
            propose to serve as an Authorized User. This means that in order for
            you to become an Authorized User, we will ask for your name,
            address, tax identification number, and other information that will
            allow us to identify you. We may also ask to see other identifying
            documents, such as a driver’s license, taxpayer identification
            number; passport number and country of issuance; alien
            identification card number; or number and country of issuance of any
            government-issued document evidencing nationality of residence and
            bearing a photograph or similar safeguard.  If we are unable to
            verify your identity to our satisfaction, you will not be permitted
            to serve as an Authorized User and you and the person who invited
            you to serve as an Authorized User will be notified.
          </p>
        </div>
      </div>
      <div className="p-8 grow ml-[500px]">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="w-full flex ml-14 items-center min-h-screen">
            {step === 0 ? (
              <div className="w-full max-w-3xl">
                <h2 className="text-4xl font-bold text-gray-900">
                  Let's Get Started
                </h2>

                <div className="mt-10 space-y-6 pb-6 ">
                  <div className="">
                    <label
                      htmlFor="first-name"
                      className="block text-sm/6 font-medium text-gray-900"
                    >
                      Full Name*
                    </label>
                    <div className="flex gap-4">
                      <div className="sm:max-w-xs flex-1">
                        <Input
                          {...register("firstName")}
                          id="firstName"
                          placeholder="First Name"
                        />
                        {errors.firstName && (
                          <div className="text-xs font-medium text-red-600 mt-1">
                            {errors.firstName?.message}
                          </div>
                        )}
                      </div>
                      <div className="sm:max-w-xs flex-1">
                        <Input
                          {...register("middleName")}
                          id="middleName"
                          placeholder="Middle Name (Optional)"
                        />
                        {errors.middleName && (
                          <div className="text-xs font-medium text-red-600 mt-1">
                            {errors.middleName?.message}
                          </div>
                        )}
                      </div>
                      <div className="sm:max-w-xs flex-1">
                        <Input
                          {...register("lastName")}
                          id="lastName"
                          placeholder="Last Name"
                        />
                        {errors.lastName && (
                          <div className="text-xs font-medium text-red-600 mt-1">
                            {errors.lastName?.message}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm/6 font-medium text-gray-900 "
                    >
                      Email Address*
                    </label>
                    <div className="sm:max-w-sm">
                      <Input
                        {...register("email")}
                        id="email"
                        type="email"
                        autoComplete="email"
                        readOnly
                      />
                      {errors.email && (
                        <div className="text-xs font-medium text-red-600 mt-1">
                          {errors.email?.message}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="">
                    <label
                      htmlFor="country"
                      className="block text-sm/6 font-medium text-gray-900 "
                    >
                      Personal Phone Number*
                    </label>
                    <div className="sm:max-w-sm">
                      <Controller
                        control={control}
                        name="phone"
                        id="phone"
                        autoComplete="phone"
                        render={({ field }) => <PhoneInput {...field} />}
                      />
                      {errors.phone && (
                        <div className="text-xs font-medium text-red-600 mt-1">
                          {errors.phone?.message}
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="street-address"
                      className="block text-sm/6 font-medium text-gray-900"
                    >
                      Social Security Number*
                    </label>
                    <div className="sm:max-w-sm">
                      <Controller
                        control={control}
                        name="socialSecurity"
                        id="socialSecurity"
                        render={({ field }) => (
                          <SocialSecurityNumberInput {...field} />
                        )}
                      />
                      {errors.socialSecurity && (
                        <div className="text-xs font-medium text-red-600 mt-1">
                          {errors.socialSecurity?.message}
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="street-address"
                      className="block text-sm/6 font-medium text-gray-900"
                    >
                      Date of Birth*
                    </label>
                    <div className="sm:max-w-sm">
                      <Controller
                        control={control}
                        name="birth"
                        id="birth"
                        render={({ field }) => (
                          //   <DatePicker
                          //     selected={field.value}
                          //     onSelect={field.onChange}
                          //     captionLayout="dropdown"
                          //     fromYear={1920}
                          //     toYear={2020}
                          //     {...field}
                          //   />
                          <RADatePicker
                            className="space-y-2"
                            {...field}
                            aria-label="date picker"
                          >
                            <div className="flex ">
                              <Group className="w-full">
                                <DateInput className="pe-9" />
                              </Group>
                              <RAButton className="z-10 -me-px -ms-9 flex w-9 items-center justify-center rounded-e-lg text-muted-foreground/80 outline-offset-2 transition-colors  focus-visible:outline-none data-[focus-visible]:outline data-[focus-visible]:outline-2 data-[focus-visible]:outline-ring/70 hover:cursor-default">
                                <CalendarIcon size={16} strokeWidth={2} />
                              </RAButton>
                            </div>
                            {/* <Popover
                              className="z-50 rounded-lg border border-border bg-background text-popover-foreground shadow-lg shadow-black/5 outline-none data-[entering]:animate-in data-[exiting]:animate-out data-[entering]:fade-in-0 data-[exiting]:fade-out-0 data-[entering]:zoom-in-95 data-[exiting]:zoom-out-95 data-[placement=bottom]:slide-in-from-top-2 data-[placement=left]:slide-in-from-right-2 data-[placement=right]:slide-in-from-left-2 data-[placement=top]:slide-in-from-bottom-2"
                              offset={4}
                            >
                              <Dialog className="max-h-[inherit] overflow-auto p-2">
                                <Calendar />
                              </Dialog>
                            </Popover> */}
                          </RADatePicker>
                        )}
                      />
                      {errors.birth && (
                        <div className="text-xs font-medium text-red-600 mt-1">
                          {errors.birth?.message}
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <div>
                      <label
                        htmlFor="street-address"
                        className="block text-sm/6 font-medium text-gray-900"
                      >
                        Personal Address*
                      </label>
                      <div className="block text-xs  text-gray-600">
                        (Cannot be a P.O box or virtual address)
                      </div>
                    </div>
                    <div className="mt-1">
                      <Input
                        {...register("address")}
                        id="address"
                        placeholder="Address Line 1"
                      />
                      {errors.address && (
                        <div className="text-xs font-medium text-red-600 mt-1">
                          {errors.address?.message}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-1">
                      <Input
                        {...register("city")}
                        id="city"
                        placeholder="City"
                      />
                      {errors.city && (
                        <div className="text-xs font-medium text-red-600 mt-1">
                          {errors.city?.message}
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <Controller
                        control={control}
                        name="state"
                        id="state"
                        render={({ field }) => {
                          return <StateSelect {...field} placeholder="State" />;
                        }}
                      />
                      {errors.state && (
                        <div className="text-xs font-medium text-red-600 mt-1">
                          {errors.state?.message}
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <Controller
                        control={control}
                        name="postalCode"
                        id="postalCode"
                        render={({ field }) => {
                          return (
                            <PostalCodeInput
                              {...field}
                              placeholder="Zip Code"
                            />
                          );
                        }}
                      />
                      {errors.postalCode && (
                        <div className="text-xs font-medium text-red-600 mt-1">
                          {errors.postalCode?.message}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex mt-6 mb-10">
                  <Button
                    type="button"
                    onClick={() => setStep(1)}
                    disabled={!isValid}
                  >
                    Continue
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            ) : step === 1 ? (
              <div>
                <div className="px-4 sm:px-0">
                  <h3 className="text-3xl font-semibold text-gray-900">
                    Confirm Your Information and Submit
                  </h3>
                </div>
                <div className="mt-6 ">
                  <dl className="">
                    <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                      <dt className="text-sm/6 font-medium text-gray-900">
                        Full name
                      </dt>
                      <dd className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0">
                        {`${getValues("firstName")}${
                          getValues("middleName") ? " " : ""
                        }${
                          getValues("middleName") ? getValues("middleName") : ""
                        } ${getValues("lastName") || ""}`.trim()}
                      </dd>
                    </div>
                    <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                      <dt className="text-sm/6 font-medium text-gray-900">
                        Email Address
                      </dt>
                      <dd className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0">
                        {`${getValues("email")}`}
                      </dd>
                    </div>
                    <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                      <dt className="text-sm/6 font-medium text-gray-900">
                        Personal Phone Number
                      </dt>
                      <dd className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0">
                        {`+1 ${formatPhoneNumber(getValues("phone"))}`}
                      </dd>
                    </div>
                    <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                      <dt className="text-sm/6 font-medium text-gray-900">
                        Social Security Number
                      </dt>
                      <dd className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0">
                        {`${formatSocialSecurityNumber(
                          getValues("socialSecurity")
                        )}`}
                      </dd>
                    </div>
                    <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                      <dt className="text-sm/6 font-medium text-gray-900">
                        Date of Birth
                      </dt>
                      <dd className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0">
                        {`${getValues("birth")?.toString()}`}
                      </dd>
                    </div>
                    <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                      <dt className="text-sm/6 font-medium text-gray-900">
                        Personal Address
                      </dt>
                      <dd className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0">
                        {`${getValues("address")}, ${getValues(
                          "city"
                        )}, ${getValues("state")} ${getValues("postalCode")}`}
                      </dd>
                    </div>
                  </dl>
                </div>
                <div className="mt-6 mb-10 flex gap-x-3 ">
                  <Button
                    variant="outline"
                    type="button"
                    onClick={() => setStep(0)}
                    disabled={isLoading}
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button disabled={isLoading || !isValid || count > 0}>
                    Submit
                    {(isLoading || count > 0) && (
                      <Loader2 className="w-4 h-4 ml-2 animate-spin" />
                    )}
                  </Button>
                </div>
              </div>
            ) : (
              <div>
                <div className="px-4 sm:px-0">
                  <h3 className="text-lg/7 font-semibold text-gray-900">
                    We’re reviewing your Authorized User application!
                  </h3>
                  <p className="max-w-4xl mt-1  text-base/6 text-gray-600">
                    Once your application has been reviewed we’ll reach out to
                    you through email. Applications usually take 1-3 business
                    days to review, but we’ll keep you updated.{" "}
                  </p>
                  <p className="max-w-4xl mt-1  text-base/6 text-gray-600">
                    Have any questions? Get in touch with us at
                    support@pocketbook.tech
                  </p>
                </div>
                <Button
                  type="button"
                  onClick={() => queryClient.invalidateQueries(["tenancy"])}
                  className="mt-4"
                >
                  Go to dashboard
                </Button>
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
